import { DirectionFindDto, type DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { UnitFindDto, type UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { CompanyFindDto, type CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { DealFindDto, type DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import { ContragentFindDto, type ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { FilialFindDto, type FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { UserGroupFindDto, type UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { FileFindDto, type FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file-find.dto';
import { BillFindDto, type BillFindInterface } from '@zaoblako/shared/modules/document/dtos/bill/bill-find.dto';
import { CurrencyFindDto, type CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { TaskDtoKeysEnum } from '@zaoblako/shared/modules/task/enum/task-dto-keys.enum';
import { DocumentDtoKeysEnum } from '@zaoblako/shared/modules/document/enums/document-dto-keys.enum';
import { MessengerDtoKeysEnum } from '@zaoblako/shared/modules/messenger/enums/messenger-dto-keys.enum';
import type { DocumentFindInterface } from '@zaoblako/shared/modules/document/dtos/document/document-find.dto';
import type { ThreadFindInterface } from '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';

import * as yup from 'yup';

/** Planned income response body */
export interface PlannedIncomeFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Planned income creation date and time */
  created: Date;
  /** Planned income last update date and time */
  updated: Date;
  /** Date of planned income */
  date: Date;
  /** Income amount in rubles */
  amount: number;
  /** Income fact amount computed by bound documents */
  factAmount: number;
  /**
   * Difference between {@link PlannedIncomeFindInterface.amount} and {@link PlannedIncomeFindInterface.factAmount}.
   * This column is not editable, it is automatically calculated by the backend.
   */
  deltaAmount: number;
  /** Is related income documents was received */
  incomesReceived?: boolean;
  /** Related company */
  company: CompanyFindInterface;
  /** Related direction */
  direction?: DirectionFindInterface;
  /** Related unit */
  unit?: UnitFindInterface;
  /** Related filial */
  filial?: FilialFindInterface;
  /** Related deal */
  deal?: DealFindInterface;
  /** Related counterparty */
  contragent?: ContragentFindInterface;
  /** Documents bound to this planned income */
  documents?: DocumentFindInterface[];
  /** Planned income creator user */
  createdBy?: UserFindInterface;
  /** Planned income responsible user */
  responsible?: UserFindInterface;
  /** Planned income user auditors */
  userAuditors?: UserFindInterface[];
  /** Planned income user auditors */
  userGroupAuditors?: UserGroupFindInterface[];
  /** Related messenger thread */
  thread?: ThreadFindInterface;
  /** Related bill */
  bill?: BillFindInterface;
  /** Related currency */
  currency?: CurrencyFindInterface;
  /** All files attached to this planned income via comments */
  readonly files?: FileFindInterface[];
  /** Number of files attached to this planned income via comments */
  readonly filesCount: number;
  /** Can current authorized user edit or delete this planned income? */
  readonly canEdit: boolean;
  /** Related currency rate (depends on selected currency and date) */
  readonly rate: number;
}

/** Planned income DTO */
export const PlannedIncomeFindDto = SerdeDtoClass.create<PlannedIncomeFindInterface>(
  TaskDtoKeysEnum.PLANNED_INCOME_FIND_DTO,
  {
    id: {
      sortable: true,
      label: 'Номер',
      serializer: yup.number(),
    },
    created: {
      label: 'Создан',
      serializer: yup.date(),
    },
    updated: {
      label: 'Обновлён',
      serializer: yup.date(),
    },
    date: {
      sortable: true,
      label: 'Дата',
      serializer: yup.date(),
    },
    amount: {
      sortable: true,
      label: 'Сумма',
      serializer: yup.number(),
    },
    factAmount: {
      sortable: true,
      label: 'Фактическая сумма',
      serializer: yup.number(),
    },
    deltaAmount: {
      sortable: true,
      label: 'Дельта',
      hint: 'Разница между полями "Сумма" и "Фактическая сумма", вычисляется бэкендом автоматически',
      serializer: yup.number(),
    },
    incomesReceived: {
      optional: true,
      label: 'Документы для поступления найдены',
      serializer: yup.boolean(),
    },
    direction: {
      optional: true,
      sortable: true,
      label: 'БС',
      nested: DirectionFindDto,
      serializer: (_, nested) => nested,
    },
    unit: {
      optional: true,
      sortable: true,
      label: 'БЕ',
      nested: UnitFindDto,
      serializer: (_, nested) => nested,
    },
    filial: {
      optional: true,
      sortable: true,
      label: 'Филиал',
      nested: FilialFindDto,
      serializer: (_, nested) => nested,
    },
    company: {
      sortable: true,
      label: 'Компания',
      nested: CompanyFindDto,
      serializer: (_, nested) => nested,
    },
    deal: {
      optional: true,
      sortable: true,
      label: 'Сделка',
      nested: DealFindDto,
      serializer: (_, nested) => nested,
    },
    contragent: {
      optional: true,
      sortable: true,
      label: 'Контрагент',
      nested: ContragentFindDto,
      serializer: (_, nested) => nested,
    },
    documents: {
      optional: true,
      label: 'Привязанные документы',
      nested: DocumentDtoKeysEnum.DOCUMENT_FIND_DTO,
      serializer: (_, nested) => yup.array(nested),
    },
    createdBy: {
      optional: true,
      sortable: true,
      label: 'Автор',
      nested: UserFindDto,
      serializer: (_, nested) => nested,
    },
    responsible: {
      sortable: true,
      optional: true,
      label: 'Ответственный',
      nested: UserFindDto,
      serializer: (_, nested) => nested,
    },
    userAuditors: {
      optional: true,
      label: 'Пользователи-аудиторы',
      nested: UserFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    userGroupAuditors: {
      optional: true,
      label: 'Группы-аудиторы',
      nested: UserGroupFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    thread: {
      optional: true,
      label: 'Комментарии к планируемому поступлению',
      nested: MessengerDtoKeysEnum.THREAD_FIND_DTO,
      serializer: (_, nested) => nested,
    },
    bill: {
      optional: true,
      sortable: true,
      label: 'Счет',
      nested: BillFindDto,
      serializer: (_, nested) => nested,
    },
    currency: {
      optional: true,
      sortable: true,
      label: 'Валюта',
      nested: CurrencyFindDto,
      serializer: (_, nested) => nested,
    },
    files: {
      optional: true,
      disabled: true,
      label: 'Все файлы, прикрепленные через комментарии',
      hint: 'Это вычисляемое поле, его нельзя редактировать',
      nested: FileFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    filesCount: {
      disabled: true,
      label: 'Количество файлов, прикрепленных через комментарии',
      hint: 'Это вычисляемое поле, его нельзя редактировать',
      serializer: yup.number(),
    },
    canEdit: {
      disabled: true,
      label: 'Может ли текущий авторизованный пользователь редактировать или удалять это поступление?',
      hint: 'Это вычисляемое поле, его нельзя редактировать',
      serializer: yup.boolean(),
    },
    rate: {
      disabled: true,
      label: 'Курс валюты на дату планируемого поступления или самый последний курс этой валюты (если курс на дату неизвестен)',
      hint: 'Это вычисляемое поле, его нельзя редактировать',
      serializer: yup.number(),
    },
  },
);
